import type { CSSProperties, ReactElement } from 'react';
import type { LobbyPlayer } from '@big2/shared';
import { useI18n } from '../../i18n.js';
import { getUserAvatar } from '../../utils/avatar.js';
import { Icon } from '../Icon.js';
import { ReactionBubble } from '../ReactionUI.js';
import type { ActiveReaction } from '../../net/socket/types.js';
import { lobbySeatStats, type LobbyStatsContext, type Tone } from './lobbySeatStats.js';

interface LobbySeatTableProps {
  readonly slots: readonly (LobbyPlayer | null)[];
  readonly myPlayerId: string | null;
  readonly selectedId: string | null;
  /** The player is picked up and the next seat tapped is where they go. */
  readonly isMoving: boolean;
  readonly canTakeEmptySeat: boolean;
  readonly emptySeatLabel: string;
  readonly reactions: Record<string, ActiveReaction>;
  readonly stats: LobbyStatsContext;
  readonly onSeatClick: (seat: number, occupant: LobbyPlayer | null) => void;
}

/** Seats ride an ellipse around the felt, as a percentage of the table box. */
const TABLE_CENTER_PCT = 50;
const SEAT_RADIUS_X_PCT = 44;
const SEAT_RADIUS_Y_PCT = 41;
const SEAT_ONE_ANGLE_DEG = 90;
const FULL_TURN_DEG = 360;

/** Seats this far above centre (as sin of their angle) put their label above the disc, clear of the felt. */
const UPPER_SEAT_SIN = -0.2;

/** Seat 1 sits at the bottom and play runs clockwise on screen, matching the game table. */
function seatPosition(seat: number, seats: number): { readonly style: CSSProperties; readonly isUpper: boolean } {
  const angle = ((SEAT_ONE_ANGLE_DEG + (seat * FULL_TURN_DEG) / seats) * Math.PI) / 180;
  return {
    style: {
      left: `${TABLE_CENTER_PCT + SEAT_RADIUS_X_PCT * Math.cos(angle)}%`,
      top: `${TABLE_CENTER_PCT + SEAT_RADIUS_Y_PCT * Math.sin(angle)}%`,
    },
    isUpper: Math.sin(angle) < UPPER_SEAT_SIN,
  };
}

function seatChipMain(stats: ReturnType<typeof lobbySeatStats>): { text: string; tone: Tone } | null {
  if (stats.tsai) return { text: `🍵${stats.tsai.balance}`, tone: stats.tsai.isLow ? 'bad' : 'neutral' };
  if (!stats.points) return null;
  const { total, limit, tone } = stats.points;
  return { text: limit ? `${total}/${limit}` : `${total}`, tone };
}

function seatLabel(seatWord: string, seat: number, occupant: LobbyPlayer | null, extras: { readonly you: string; readonly empty: string; readonly offline: string }, isSelf: boolean): string {
  if (!occupant) return `${seatWord} ${seat + 1}: ${extras.empty}`;
  const status = !occupant.isBot && !occupant.connected ? `, ${extras.offline}` : '';
  return `${seatWord} ${seat + 1}: ${occupant.name}${isSelf ? extras.you : ''}${status}`;
}

function discContent(occupant: LobbyPlayer | null, seat: number, initial: string | undefined): string | number {
  if (!occupant) return seat + 1;
  return occupant.isBot ? '♠' : initial ?? '';
}

function SeatChip({ player, stats }: { readonly player: LobbyPlayer; readonly stats: LobbyStatsContext }): ReactElement | null {
  const seatStats = lobbySeatStats(player, stats);
  const main = seatChipMain(seatStats);
  const { delta } = seatStats;
  if (!main && !delta) return null;
  return (
    <span className="lobby-mini-seat-chip" aria-hidden="true">
      {main && <span className={`is-${main.tone}`}>{main.text}</span>}
      {delta && <span className={`is-${delta.tone}`}>{delta.text}</span>}
    </span>
  );
}

/**
 * The lobby's player roster, drawn as a small top-down table.
 * Tapping a player opens their details; tapping an empty seat sits you there (or places the player being moved).
 */
export function LobbySeatTable({
  slots, myPlayerId, selectedId, isMoving, canTakeEmptySeat, emptySeatLabel, reactions, stats, onSeatClick,
}: LobbySeatTableProps): ReactElement {
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';
  const seatWord = isMn ? 'Суудал' : 'Seat';
  const labelExtras = {
    you: isMn ? ' (та)' : ' (you)',
    empty: isMn ? 'хоосон' : 'empty',
    offline: t.offline.toLowerCase(),
  };

  return (
    <div
      className={`lobby-mini-table seats-${slots.length}${isMoving ? ' is-moving' : ''}`}
      role="group"
      aria-label={isMn ? 'Ширээний суудлууд' : 'Table seats'}
    >
      <div className="lobby-mini-table-felt" aria-hidden="true" />
      {slots.map((occupant, seat) => {
        const isSelf = !!occupant && occupant.playerId === myPlayerId;
        const isSelected = !!occupant && occupant.playerId === selectedId;
        const isPaid = occupant?.tier === 'paid';
        const avatar = occupant && !occupant.isBot ? getUserAvatar(occupant.userId || occupant.playerId, occupant.name) : null;
        const canAct = occupant ? !isMoving || !occupant.isBot : canTakeEmptySeat || isMoving;
        const label = seatLabel(seatWord, seat, occupant, labelExtras, isSelf);
        const reaction = occupant ? reactions[occupant.playerId] : undefined;
        const position = seatPosition(seat, slots.length);
        const classes = [
          'lobby-mini-seat',
          occupant ? 'is-taken' : 'is-empty',
          position.isUpper && 'is-upper',
          isSelf && 'is-self',
          isSelected && 'is-selected',
          occupant?.isHost && 'is-host',
        ].filter(Boolean).join(' ');

        return (
          <button
            key={seat}
            type="button"
            className={classes}
            style={position.style}
            disabled={!canAct}
            aria-pressed={occupant ? isSelected : undefined}
            aria-label={label}
            title={label}
            onClick={() => onSeatClick(seat, occupant)}
          >
            <span
              className={`lobby-mini-seat-disc${occupant?.isBot ? ' is-bot' : ''}${isPaid ? ' is-vip' : ''}`}
              style={avatar ? { background: avatar.gradient } : undefined}
              aria-hidden="true"
            >
              {discContent(occupant, seat, avatar?.initial)}
              {occupant && !occupant.isBot && (
                <span className={`lobby-online-dot${occupant.connected ? '' : ' is-offline'}`} />
              )}
              {occupant?.isHost && <span className="lobby-mini-seat-host">♔</span>}
              {isPaid && (
                <span className="lobby-mini-seat-vip room-vip-tag"><Icon name="crown" size={10} /></span>
              )}
            </span>
            <span className="lobby-mini-seat-name" aria-hidden="true">
              {occupant ? occupant.name : emptySeatLabel}
            </span>
            {occupant && <SeatChip player={occupant} stats={stats} />}
            {reaction && (
              <span className="lobby-mini-seat-reaction" aria-live="polite">
                <ReactionBubble reactionId={reaction.reactionId} seq={reaction.seq} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
